/**
 * Client-side entry point for a single-player session.
 *
 * Deliberately thin. All it does is create a `LocalSession` and hand back the
 * `Transport` it exposes — because the client above this line is written against
 * `Transport`, not against the session. When Phase 10 adds a WebSocket client, it
 * implements the same interface and `main.ts` changes by one line: which factory
 * it calls.
 *
 * The simulation runs in this browser tab. That is not a shortcut around the
 * server-authoritative model — it *is* the model, with the network hop removed.
 * The same `server/src/game` code will run behind a socket later, unchanged.
 */

import type { Transport } from "kngdm-shared";
import type { BotDifficulty } from "kngdm-server/local";
import { LocalSession } from "kngdm-server/local";

export interface SinglePlayerOptions {
  readonly seed?: number;
  readonly kingdomCount?: number;
  readonly sessionSeconds?: number;
  readonly difficulty?: BotDifficulty;
  readonly width?: number;
  readonly height?: number;
}

export interface SinglePlayerGame {
  readonly transport: Transport;
  readonly session: LocalSession;
  start(): void;
  stop(): void;
}

export function createSinglePlayerGame(options: SinglePlayerOptions = {}): SinglePlayerGame {
  const session = new LocalSession({ ...options, humanKingdomIndex: 0 });
  const transport = session.connect();

  return {
    transport,
    session,
    start: () => session.start(),
    stop: () => session.stop(),
  };
}
